"use client";

import { useEffect, useState } from "react";

// Tempo máximo absoluto (ms) que o preloader pode ficar visível.
const MAX_WAIT_MS = 6000;

// Tempo que o DOM fica estável pra considerarmos que os plugins já terminaram
// de montar/estilizar os elementos.
const DOM_QUIET_MS = 400;

// Teto de espera de estabilização do DOM
const DOM_QUIET_CAP_MS = 4000;

function waitForStylesheets() {
    if (typeof document === "undefined") return Promise.resolve();

    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    const pending = links.filter((link) => !link.sheet);

    if (pending.length === 0) return Promise.resolve();

    return Promise.all(
        pending.map(
            (link) =>
                new Promise((resolve) => {
                    link.addEventListener("load", resolve, { once: true });
                    // se um CSS der erro (404, CORS, etc.) não travamos o site esperando ele
                    link.addEventListener("error", resolve, { once: true });
                })
        )
    );
}

function waitForFonts() {
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
        return document.fonts.ready;
    }
    return Promise.resolve();
}

function waitForDomToSettle() {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        let finished = false;
        let settleTimer;

        const finish = () => {
            if (finished) return;
            finished = true;
            clearTimeout(settleTimer);
            clearTimeout(hardCapTimer);
            observer.disconnect();
            resolve();
        };

        const scheduleSettle = () => {
            clearTimeout(settleTimer);
            settleTimer = setTimeout(finish, DOM_QUIET_MS);
        };

        const observer = new MutationObserver(scheduleSettle);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        });

        const hardCapTimer = setTimeout(finish, DOM_QUIET_CAP_MS);

        scheduleSettle();
    });
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function SitePreloader() {
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        Promise.race([
            Promise.all([waitForStylesheets(), waitForFonts(), waitForDomToSettle()]),
            delay(MAX_WAIT_MS),
        ]).then(() => {
            if (!cancelled) setIsReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div
            className={`site-preloader${isReady ? " site-preloader--hidden" : ""}`}
            role="status"
            aria-live="polite"
            aria-busy={!isReady}
        >
            <div className="site-preloader__spinner" />
        </div>
    );
}